import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { Authentticatevalues, ROUTES, STORAGE_KEYS } from '@/constants';
import { getLastTwoUserLogEvents } from '@/service/userLogService';
import { useAppSelector, selectUser, selectPlant, selectSessionId } from '@/store';

const SKIP_PATHS = new Set<string>([
  ROUTES.login,
  ROUTES.otp,
  ROUTES.signup,
  ROUTES.plantSelection,
  ROUTES.changePlant,
  ROUTES.workModeSelection,
  ROUTES.breakMode,
  ROUTES.shuttleTransfer,
  ROUTES.logoutSummary,
  ROUTES.maintenance,
  ROUTES.error,
  ROUTES.restrictedArea,
]);

export function StartupRedirectGuard() {
  const navigate = useNavigate();
  const location = useLocation();
  const user = useAppSelector(selectUser);
  const plant = useAppSelector(selectPlant);
  const sessionId = useAppSelector(selectSessionId);
  const hasChecked = useRef(false);

  useEffect(() => {
    if (hasChecked.current) return;
    if (!user || !plant || !sessionId) return;
    if (SKIP_PATHS.has(location.pathname)) return;

    hasChecked.current = true;

    void (async () => {
      try {
        const response = await getLastTwoUserLogEvents(String(plant.org_id), user._id);
        const events = response.Events;

        if (events.length < 2) return;

        const lastActivity = events[0]?.UserAction ?? '';
        const previousActivity = events[1]?.UserAction ?? '';

        if (
          lastActivity === Authentticatevalues.UserStatusBreak &&
          previousActivity === Authentticatevalues.UserStatusEndYardWork
        ) {
          localStorage.setItem(STORAGE_KEYS.breakModeActive, 'true');
          localStorage.setItem(STORAGE_KEYS.breakModeFrom, '');
          navigate(ROUTES.breakMode, { replace: true });
          return;
        }

        if (
          lastActivity === Authentticatevalues.UserStatusStartShuttleTransfer &&
          previousActivity === Authentticatevalues.UserStatusEndYardWork
        ) {
          localStorage.setItem(STORAGE_KEYS.shuttleModeActive, 'true');
          navigate(ROUTES.shuttleTransfer, { replace: true });
          return;
        }

        if (
          lastActivity === Authentticatevalues.UserStatusContinue &&
          previousActivity === Authentticatevalues.UserStatusBreak
        ) {
          localStorage.setItem(STORAGE_KEYS.shuttleModeActive, 'true');
          navigate(ROUTES.shuttleTransfer, { replace: true });
          return;
        }

        if (
          lastActivity === Authentticatevalues.UserStatusBreak &&
          previousActivity === Authentticatevalues.UserStatusStopShuttleTransfer
        ) {
          localStorage.setItem(STORAGE_KEYS.breakModeActive, 'true');
          localStorage.setItem(STORAGE_KEYS.breakModeFrom, 'shuttle');
          navigate(`${ROUTES.breakMode}?from=shuttle`, { replace: true });
          return;
        }
      } catch {
        // API failure — stay on current route (dashboard)
      }
    })();
  }, [user, plant, sessionId, navigate, location.pathname]);

  return null;
}
